import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmAlertDialogImports } from '@spartan-ng/helm/alert-dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { firstValueFrom, forkJoin } from 'rxjs';
import { CompanyService } from '../../services/company.service';
import { LoggerService } from '../../services/logger.service';
import {
  CatalogEntry,
  DesignElement,
  designErrorMessage,
  DesignLayout,
  LayoutIssue,
  templateKeyForArtwork,
  VoucherDesign,
  VoucherDesignService,
  VoucherType,
  voucherTypeLabel,
} from '../../services/voucher-design.service';
import { EditorToolbarComponent } from './components/editor-toolbar/editor-toolbar.component';
import { ElementPaletteComponent } from './components/element-palette/element-palette.component';
import { ElementInspectorComponent } from './components/element-inspector/element-inspector.component';
import { DesignCanvasComponent } from './components/design-canvas/design-canvas.component';
import { PreviewDialogComponent } from './components/preview-dialog/preview-dialog.component';
import { PagePanelComponent } from './components/page-panel/page-panel.component';
import { SelectionPanelComponent } from './components/selection-panel/selection-panel.component';
import { ShortcutsDialogComponent } from './components/shortcuts-dialog/shortcuts-dialog.component';
import { MobileSummaryComponent } from './components/mobile-summary/mobile-summary.component';
import { BackgroundTemplateDialogComponent } from './components/background-template-dialog/background-template-dialog.component';
import { ImageLibraryDialogComponent } from './components/image-library-dialog/image-library-dialog.component';
import { CompanyImageService, DesignImage } from '../../services/company-image.service';
import { Rect } from './editor-geometry';
import { BoxShape } from './shared/shapes';
import { LeaveAware } from './voucher-design-editor.guard';
import {
  PageSettings,
  PatchScope,
  SelectionPatch,
  TextPreset,
  VoucherDesignEditorStore,
  cleanElement,
  pageSettingsOf,
} from './voucher-design-editor.store';

/** What the editor writes to the clipboard: its elements, told apart from any other text. */
const CLIP_TYPE = 'schenkwerk/voucher-design-elements';

interface ClipPayload {
  type: typeof CLIP_TYPE;
  elements: DesignElement[];
}

function parseClip(text: string | undefined): DesignElement[] | null {
  if (!text?.startsWith('{')) return null;
  try {
    const payload = JSON.parse(text) as Partial<ClipPayload>;
    return payload.type === CLIP_TYPE && Array.isArray(payload.elements) ? payload.elements : null;
  } catch {
    return null;
  }
}

/** The design editor: place the voucher's elements on the company's own artwork. */
@Component({
  selector: 'app-voucher-design-editor',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmAlertDialogImports,
    HlmButtonImports,
    HlmSkeletonImports,
    HlmTypographyImports,
    EditorToolbarComponent,
    ElementPaletteComponent,
    ElementInspectorComponent,
    DesignCanvasComponent,
    PreviewDialogComponent,
    PagePanelComponent,
    SelectionPanelComponent,
    ShortcutsDialogComponent,
    MobileSummaryComponent,
    BackgroundTemplateDialogComponent,
    ImageLibraryDialogComponent,
  ],
  providers: [VoucherDesignEditorStore, provideIcons({ lucideTriangleAlert })],
  templateUrl: './voucher-design-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoucherDesignEditorComponent implements OnInit, OnDestroy, LeaveAware {
  readonly store = inject(VoucherDesignEditorStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly designService = inject(VoucherDesignService);
  private readonly imageService = inject(CompanyImageService);
  private readonly companyService = inject(CompanyService);
  private readonly logger = inject(LoggerService);

  private readonly toolbar = viewChild<EditorToolbarComponent>('toolbar');

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly notFound = signal(false);
  readonly saving = signal(false);
  readonly scaling = signal(false);
  readonly copying = signal(false);
  readonly changingBackground = signal(false);
  readonly templateDialogOpen = signal(false);
  /** Why the image library is open: to place a new picture or to swap the selected one. */
  readonly libraryMode = signal<'add' | 'replace' | null>(null);
  readonly shortcutsOpen = signal(false);
  /** A phone-sized viewport gets the summary instead of the full editor, unless asked. */
  readonly compact = signal(false);
  readonly forceFull = signal(false);
  /** Only the panels that are shown get a column: a hidden one leaves the grid. */
  readonly gridColumns = computed(() =>
    [
      this.store.paletteOpen() ? '16rem' : null,
      'minmax(0,1fr)',
      this.store.inspectorOpen() ? '18rem' : null,
    ]
      .filter(Boolean)
      .join(' '),
  );
  /** The library pictures the layout refers to, by id: object URLs for the canvas. */
  readonly pictures = signal<Map<string, { src: string; width: number; height: number }>>(
    new Map(),
  );
  private readonly loadingPictures = new Set<string>();
  readonly previewing = signal(false);
  readonly previewOpen = signal(false);
  readonly previewImageUrl = signal<string | null>(null);
  readonly previewError = signal<string | null>(null);
  readonly downloadingPdf = signal(false);
  readonly actionError = signal<string | null>(null);
  readonly leaveDialogOpen = signal(false);
  readonly activateDialogOpen = signal(false);
  private leaveResolver: ((leave: boolean) => void) | null = null;
  /** The last copy, for a paste the browser hands us without clipboard text. */
  private clipboard: DesignElement[] = [];

  /** From the route; a copy navigates to the new design, which reloads the editor. */
  readonly designId = signal(this.route.snapshot.paramMap.get('id') ?? '');
  /** The canvas background: the upload's PNG, the template's artwork, or nothing. */
  readonly previewSrc = computed(() => {
    const design = this.store.design();
    if (!design) return '';
    if (design.background_kind === 'template' && design.background_template) {
      // the artwork draws a "Gültig bis" box only while a text prints the date
      return this.designService.templateArtworkUrl(
        design.background_template,
        this.store.usesExpiry(),
      );
    }
    return this.designService.previewImageUrl(design);
  });
  readonly pageColor = computed(() => {
    const design = this.store.design();
    return design?.background_kind === 'none' ? (design.background_color ?? '#ffffff') : null;
  });
  /** The company's logo and banner (public URLs) and library pictures (object URLs). */
  readonly imageSrcFor = computed(() => {
    const company = this.companyService.company();
    const byKind = this.store.catalogByKind();
    const pictures = this.pictures();
    const base = this.companyService.getApiBaseUrl();
    const publicUrl = (path: string | undefined) =>
      path ? `${base}/images/${path.startsWith('uploads/') ? path.substring(8) : path}` : null;
    return (element: DesignElement): string | null => {
      const source = byKind.get(element.kind)?.image_source;
      if (source === 'logo') return publicUrl(company?.logo_image_path);
      if (source === 'banner') return publicUrl(company?.banner_image_path);
      if (source === 'library') return pictures.get(element.image_id ?? '')?.src ?? null;
      return null;
    };
  });
  readonly selectedPicture = computed(() => {
    const selected = this.store.selected();
    return selected?.image_id ? (this.pictures().get(selected.image_id) ?? null) : null;
  });
  readonly usedImageIds = computed(
    () =>
      new Set(
        this.store
          .elements()
          .map((e) => e.image_id)
          .filter((id): id is string => !!id),
      ),
  );
  readonly activateText = computed(() => {
    const type = voucherTypeLabel(this.store.voucherType());
    return `Neue PDFs für ${type}e verwenden ab sofort dieses Design. Das bisherige Design bleibt gespeichert.`;
  });

  constructor() {
    this.store.pageRestorer = (page, elements) => this.restorePage(page, elements);
    const media = window.matchMedia('(max-width: 1023px)');
    this.compact.set(media.matches);
    const onMedia = (event: MediaQueryListEvent) => this.compact.set(event.matches);
    media.addEventListener('change', onMedia);
    inject(DestroyRef).onDestroy(() => media.removeEventListener('change', onMedia));
    effect(() => {
      const company = this.companyService.company();
      this.store.branding.set({
        logo: !!company?.logo_image_path,
        banner: !!company?.banner_image_path,
      });
      const withDate = company?.legal_min_validity_years_with_date;
      const withoutDate = company?.legal_min_validity_years_without_date;
      this.store.legalYears.set(
        withDate != null && withoutDate != null ? { withDate, withoutDate } : null,
      );
    });
    // Pictures the layout refers to but the canvas has not loaded yet (a loaded design).
    effect(() => {
      const missing = [...this.usedImageIds()].filter(
        (id) => !this.pictures().has(id) && !this.loadingPictures.has(id),
      );
      if (missing.length) untracked(() => this.loadPictures(missing));
    });
  }

  private loadPictures(ids: string[]) {
    ids.forEach((id) => this.loadingPictures.add(id));
    this.imageService.list().subscribe({
      next: ({ images }) => {
        for (const id of ids) {
          const image = images.find((i) => i.public_id === id);
          if (image) this.fetchPicture(image);
          else this.loadingPictures.delete(id); // deleted meanwhile: stays a placeholder
        }
      },
      error: (error) => {
        ids.forEach((id) => this.loadingPictures.delete(id));
        this.logger.error('Bilder konnten nicht geladen werden:', error);
      },
    });
  }

  private fetchPicture(image: DesignImage, then?: () => void) {
    this.loadingPictures.add(image.public_id);
    this.imageService.fileBlob(image).subscribe({
      next: (blob) => {
        this.loadingPictures.delete(image.public_id);
        this.pictures.update((all) => {
          const next = new Map(all);
          const previous = next.get(image.public_id);
          if (previous) URL.revokeObjectURL(previous.src);
          next.set(image.public_id, {
            src: URL.createObjectURL(blob),
            width: image.width_px,
            height: image.height_px,
          });
          return next;
        });
        then?.();
      },
      error: (error) => {
        this.loadingPictures.delete(image.public_id);
        this.logger.error('Bild konnte nicht geladen werden:', error);
      },
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id') ?? '';
      if (id !== this.designId() || !this.store.design()) {
        this.designId.set(id);
        this.load();
      }
    });
  }

  ngOnDestroy() {
    this.replacePreviewImage(null);
    for (const picture of this.pictures().values()) URL.revokeObjectURL(picture.src);
  }

  load() {
    this.loading.set(true);
    this.loadError.set(null);
    this.notFound.set(false);
    forkJoin({
      design: this.designService.get(this.designId()),
      catalog: this.designService.catalog(),
    }).subscribe({
      next: ({ design, catalog }) => {
        this.store.init(design, catalog);
        this.loading.set(false);
      },
      error: (error) => {
        this.logger.error('Design konnte nicht geladen werden:', error);
        if (error?.status === 404) this.notFound.set(true);
        else this.loadError.set('Das Design konnte nicht geladen werden.');
        this.loading.set(false);
      },
    });
  }

  // ------------------------------------------------------------ editing

  addElement(kind: string) {
    const element = this.store.addElement(kind);
    if (element) this.focusElement(element.id);
  }

  addShape(shape: BoxShape) {
    const element = this.store.addElement('box', undefined, shape);
    if (element) this.focusElement(element.id);
  }

  addText(preset: TextPreset) {
    const element = this.store.addText(preset);
    if (!element) return;
    // a fresh text opens in place with its words selected: typing replaces them
    if (preset.edit) this.store.startEditing(element.id, true);
    else this.focusElement(element.id);
  }

  patchSelected(patch: Partial<DesignElement>) {
    const selected = this.store.selected();
    if (selected) this.store.patchElement(selected.id, patch);
  }

  /** A shared change from the selection panel: only the elements of its scope take it. */
  patchSelection({ scope, patch }: { scope: PatchScope; patch: SelectionPatch }) {
    const accepts: Record<PatchScope, (entry: CatalogEntry | undefined) => boolean> = {
      text: (entry) => entry?.render === 'text',
      frame: (entry) => (entry?.properties ?? []).includes('border'),
      box: (entry) => entry?.render === 'box',
      qr: (entry) => entry?.render === 'qr',
      opacity: (entry) => (entry?.properties ?? []).includes('opacity'),
    };
    const keys = Object.keys(patch.font ?? patch).join(',');
    this.store.patchSelection(patch, (_, entry) => accepts[scope](entry), `${scope}:${keys}`);
  }

  removeSelected() {
    this.store.removeSelected();
  }

  moveSelected(delta: number) {
    const selected = this.store.selected();
    if (selected) this.store.move(selected.id, delta);
  }

  // ------------------------------------------------------------ pictures

  openLibrary(mode: 'add' | 'replace') {
    this.libraryMode.set(mode);
  }

  /** The library's pick: a new `image` element, or the selected element's new picture. */
  onPicturePicked(image: DesignImage) {
    const mode = this.libraryMode();
    this.libraryMode.set(null);
    const picture = { id: image.public_id, width: image.width_px, height: image.height_px };
    if (mode === 'replace' && this.store.selected()) {
      this.patchSelected({ image_id: image.public_id });
    } else {
      const element = this.store.addElement('image', picture);
      if (element) this.focusElement(element.id);
    }
    if (!this.pictures().has(image.public_id)) this.fetchPicture(image);
  }

  focusElement(id: string) {
    this.store.select(id);
    setTimeout(() => {
      const surface = document.querySelector<HTMLElement>(`[data-id="${id}"] button.surface`);
      surface?.focus({ preventScroll: false });
    });
  }

  /** Typing into a field: the editor's shortcuts and clipboard stay out of it. */
  private inField(target: EventTarget | null): boolean {
    return !!(target as HTMLElement | null)?.matches?.(
      'input, textarea, select, [contenteditable]',
    );
  }

  // ----------------------------------------------------------- clipboard

  /** ⌘C: the selection as text on the clipboard, so it travels to other designs and tabs. */
  @HostListener('document:copy', ['$event'])
  onCopy(event: ClipboardEvent) {
    if (this.inField(event.target) || this.anyDialogOpen()) return;
    const copies = this.store.copySelection();
    if (copies.length === 0) return;
    event.preventDefault();
    this.clipboard = copies;
    const payload: ClipPayload = { type: CLIP_TYPE, elements: copies };
    event.clipboardData?.setData('text/plain', JSON.stringify(payload));
  }

  @HostListener('document:cut', ['$event'])
  onCut(event: ClipboardEvent) {
    if (this.inField(event.target) || this.anyDialogOpen()) return;
    const copies = this.store.cutSelection();
    if (copies.length === 0) return;
    event.preventDefault();
    this.clipboard = copies;
    const payload: ClipPayload = { type: CLIP_TYPE, elements: copies };
    event.clipboardData?.setData('text/plain', JSON.stringify(payload));
  }

  /** ⌘V: elements the editor put on the clipboard (any other text is none of ours). */
  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    if (this.inField(event.target) || this.anyDialogOpen()) return;
    const copies = parseClip(event.clipboardData?.getData('text/plain')) ?? this.clipboard;
    if (copies.length === 0) return;
    event.preventDefault();
    this.placeCopies(copies);
  }

  /** ⌘D and the panels' button: the selection once more, next to itself. */
  duplicate() {
    this.placeCopies(this.store.copySelection());
  }

  private placeCopies(copies: DesignElement[]) {
    const ids = this.store.paste(copies);
    if (ids.length) this.focusElement(ids[0]);
    else if (copies.length)
      this.actionError.set(
        'Nichts eingefügt: das Element gibt es auf dieser Seite schon, oder es gehört nur auf Produkt-Gutscheine.',
      );
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();
    const inField = this.inField(event.target);
    if (mod && key === 's') {
      event.preventDefault();
      if (this.store.canSave() && this.store.dirty()) this.save();
    } else if (mod && key === 'z' && !inField) {
      event.preventDefault();
      if (event.shiftKey) this.store.redo();
      else this.store.undo();
    } else if (mod && key === 'y' && !inField) {
      event.preventDefault();
      this.store.redo();
    } else if (event.key === '?' && !inField && !this.anyDialogOpen()) {
      event.preventDefault();
      this.shortcutsOpen.set(true);
    } else if (mod && key === 'a' && !inField && !this.anyDialogOpen()) {
      event.preventDefault();
      this.store.selectAll();
    } else if (mod && key === 'g' && !inField) {
      event.preventDefault();
      if (event.shiftKey) this.store.ungroup();
      else this.store.group();
    } else if (mod && key === 'd' && !inField && !this.anyDialogOpen()) {
      event.preventDefault(); // the browser would bookmark the page
      this.duplicate();
    } else if (
      mod &&
      event.shiftKey &&
      (event.code === 'Period' || event.code === 'Comma') &&
      !inField
    ) {
      // physical keys, so it works on a German keyboard too (⇧. is : there)
      event.preventDefault();
      this.store.adjustFontSize(event.code === 'Period' ? 1 : -1);
    } else if (
      event.key === 'Escape' &&
      !this.leaveDialogOpen() &&
      !this.activateDialogOpen() &&
      !this.libraryMode() &&
      !this.templateDialogOpen()
    ) {
      this.store.select(null);
    }
  }

  private anyDialogOpen(): boolean {
    return (
      this.leaveDialogOpen() ||
      this.activateDialogOpen() ||
      !!this.libraryMode() ||
      this.templateDialogOpen() ||
      this.previewOpen() ||
      this.shortcutsOpen()
    );
  }

  /** Place everything required that is missing and focus the first of them. */
  addRequired() {
    const ids = this.store.addRequired();
    if (ids.length) this.focusElement(ids[0]);
  }

  /** The canvas's drag: every rect of the selection, continuously or on release. */
  /** ⌥ + drag on a picture: the crop moves while dragging, one history entry at the end. */
  onCanvasFocus(change: { id: string; focus_x: number; focus_y: number; commit: boolean }) {
    const patch = { focus_x: change.focus_x, focus_y: change.focus_y };
    if (change.commit) this.store.commitPatch(change.id, patch);
    else this.store.patchElement(change.id, patch, true);
  }

  onCanvasRects(changes: { id: string; rect: Rect }[], commit: boolean) {
    for (const { id, rect } of changes) {
      if (commit) this.store.setRect(id, rect);
      else this.store.patchElement(id, rect, true);
    }
  }

  /** The server's objections to the last save first, then the editor's own checks. */
  readonly allErrors = computed(() => [
    ...this.store.serverIssues(),
    ...this.store.validation().errors,
  ]);

  readonly selectionHasGroups = computed(() =>
    this.store.selectedElements().some((e) => !!e.group),
  );

  // ----------------------------------------------------------- persisting

  private layout(): DesignLayout {
    return { version: 2, elements: this.store.elements().map(cleanElement) };
  }

  save(afterSave?: () => void) {
    if (this.saving()) return;
    this.saving.set(true);
    this.actionError.set(null);
    this.designService.saveLayout(this.designId(), this.layout()).subscribe({
      next: (design) => {
        this.store.markSaved(design);
        this.saving.set(false);
        this.toolbar()?.showSaved('Layout gespeichert');
        afterSave?.();
      },
      error: (error) => {
        this.logger.error('Layout konnte nicht gespeichert werden:', error);
        this.saving.set(false);
        const detail = error?.error?.detail;
        if (error?.status === 422 && Array.isArray(detail)) {
          this.store.setServerIssues(detail as LayoutIssue[]);
          this.toolbar()?.showSaveError('Das Layout wurde nicht angenommen, siehe Hinweise.');
        } else {
          this.toolbar()?.showSaveError(
            designErrorMessage(error, 'Das Layout konnte nicht gespeichert werden.'),
          );
        }
      },
    });
  }

  rename(name: string) {
    this.designService.rename(this.designId(), name).subscribe({
      next: (design) => this.store.design.set({ ...design, layout: this.store.design()!.layout }),
      error: (error) => {
        this.logger.error('Umbenennen fehlgeschlagen:', error);
        this.actionError.set(
          designErrorMessage(error, 'Der Name konnte nicht gespeichert werden.'),
        );
      },
    });
  }

  /** The DEMO voucher with the current (also unsaved) layout: PNG in a dialog, PDF to download. */
  preview() {
    this.previewOpen.set(true);
    this.loadPreviewImage();
  }

  loadPreviewImage() {
    if (this.previewing()) return;
    this.previewing.set(true);
    this.previewError.set(null);
    const options = { longTexts: this.store.longTexts() };
    this.designService.previewPng(this.designId(), this.layout(), options).subscribe({
      next: (blob) => {
        this.replacePreviewImage(URL.createObjectURL(blob));
        this.previewing.set(false);
      },
      error: (error) => {
        this.logger.error('Vorschau fehlgeschlagen:', error);
        this.previewing.set(false);
        this.previewError.set(
          designErrorMessage(error, 'Die Vorschau konnte nicht erstellt werden.'),
        );
      },
    });
  }

  closePreview() {
    this.previewOpen.set(false);
    this.replacePreviewImage(null);
  }

  downloadPreviewPdf() {
    if (this.downloadingPdf()) return;
    this.downloadingPdf.set(true);
    const options = { longTexts: this.store.longTexts() };
    this.designService.previewPdf(this.designId(), this.layout(), options).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `vorschau_${this.store.design()?.name ?? 'design'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.downloadingPdf.set(false);
      },
      error: (error) => {
        this.logger.error('PDF-Vorschau fehlgeschlagen:', error);
        this.downloadingPdf.set(false);
        this.previewError.set(designErrorMessage(error, 'Das PDF konnte nicht erstellt werden.'));
      },
    });
  }

  private replacePreviewImage(url: string | null) {
    const previous = this.previewImageUrl();
    if (previous) URL.revokeObjectURL(previous);
    this.previewImageUrl.set(url);
  }

  /** About the real size on screen: one CSS inch per inch (screens vary). */
  showRealSize() {
    this.store.setZoom(1);
  }

  /** Print bigger or smaller; unsaved element changes are saved first. */
  applyPrintScale(scale: number) {
    if (this.store.dirty()) {
      if (!this.store.canSave()) {
        this.actionError.set('Bitte zuerst die offenen Punkte im Layout beheben.');
        return;
      }
      this.save(() => this.setPrintScale(scale));
    } else {
      this.setPrintScale(scale);
    }
  }

  private setPrintScale(scale: number) {
    this.scaling.set(true);
    this.actionError.set(null);
    const before = this.store.design()!;
    this.designService.setPrintScale(this.designId(), scale).subscribe({
      next: (design) => {
        this.store.commitPageChange(before, design);
        this.scaling.set(false);
      },
      error: (error) => {
        this.logger.error('Druckgröße konnte nicht geändert werden:', error);
        this.scaling.set(false);
        this.actionError.set(
          designErrorMessage(error, 'Die Druckgröße konnte nicht geändert werden.'),
        );
      },
    });
  }

  /** A copy of the saved layout, as the same or the other voucher type; unsaved changes are saved first. */
  copy(voucherType: VoucherType | null) {
    if (this.copying()) return;
    if (this.store.dirty()) {
      if (!this.store.canSave()) {
        this.actionError.set('Bitte zuerst die offenen Punkte im Layout beheben.');
        return;
      }
      this.save(() => this.runCopy(voucherType));
    } else {
      this.runCopy(voucherType);
    }
  }

  private runCopy(voucherType: VoucherType | null) {
    this.copying.set(true);
    this.actionError.set(null);
    const options = voucherType ? { voucher_type: voucherType } : {};
    this.designService.copy(this.designId(), options).subscribe({
      next: (copy) => {
        this.copying.set(false);
        this.router.navigate(['/voucher-designs', copy.public_id]);
      },
      error: (error) => {
        this.logger.error('Kopieren fehlgeschlagen:', error);
        this.copying.set(false);
        this.actionError.set(designErrorMessage(error, 'Das Design konnte nicht kopiert werden.'));
      },
    });
  }

  // ----------------------------------------------------------- background

  /** Background changes are saved on the server; unsaved element changes go first. */
  private withSavedLayout(then: () => void) {
    if (!this.store.dirty()) {
      then();
      return;
    }
    if (!this.store.canSave()) {
      this.actionError.set('Bitte zuerst die offenen Punkte im Layout beheben.');
      return;
    }
    this.save(then);
  }

  setBackgroundFile(file: File) {
    this.withSavedLayout(() =>
      this.runBackgroundChange(this.designService.setBackgroundFile(this.designId(), file), {
        done: (design) => this.store.rememberFile(design.preview_url, file),
      }),
    );
  }

  setOrientation(landscape: boolean) {
    this.withSavedLayout(() =>
      this.runBackgroundChange(this.designService.setOrientation(this.designId(), landscape), {
        failed: 'Die Seite konnte nicht gedreht werden.',
      }),
    );
  }

  openTemplateDialog() {
    this.templateDialogOpen.set(true);
  }

  setBackgroundTemplate(templateKey: string) {
    this.withSavedLayout(() =>
      this.runBackgroundChange(
        this.designService.setBackgroundTemplate(this.designId(), templateKey),
        { done: () => this.templateDialogOpen.set(false) },
      ),
    );
  }

  removeBackground() {
    const color = this.store.design()?.background_color ?? '#ffffff';
    this.withSavedLayout(() =>
      this.runBackgroundChange(this.designService.removeBackground(this.designId(), color)),
    );
  }

  setPageColor(color: string) {
    this.withSavedLayout(() =>
      this.runBackgroundChange(this.designService.setPageColor(this.designId(), color), {
        failed: 'Die Seitenfarbe konnte nicht geändert werden.',
      }),
    );
  }

  /** A page change the server answers with the reworked design; one history entry. */
  private runBackgroundChange(
    request: ReturnType<VoucherDesignService['setPageColor']>,
    options: { done?: (design: VoucherDesign) => void; failed?: string } = {},
  ) {
    if (this.changingBackground()) return;
    this.changingBackground.set(true);
    this.actionError.set(null);
    const before = this.store.design()!;
    request.subscribe({
      next: (design) => {
        this.store.commitPageChange(before, design);
        this.changingBackground.set(false);
        options.done?.(design);
        if (design.warnings?.length) this.toolbar()?.showSaved(design.warnings.join(' '));
      },
      error: (error) => {
        this.logger.error('Seite konnte nicht geändert werden:', error);
        this.changingBackground.set(false);
        this.actionError.set(
          designErrorMessage(
            error,
            options.failed ?? 'Der Hintergrund konnte nicht geändert werden.',
          ),
        );
      },
    });
  }

  /**
   * Undo or redo across a page change: put the background, the orientation and
   * the print size back the way they were, then save the elements of that moment
   * so nothing is left unsaved. The store swaps the elements in once this resolves.
   */
  private async restorePage(page: PageSettings, elements: DesignElement[]): Promise<VoucherDesign> {
    const id = this.designId();
    const api = this.designService;
    this.changingBackground.set(true);
    this.actionError.set(null);
    try {
      let design = this.store.design()!;
      const now = () => pageSettingsOf(design);
      if (page.background_kind === 'none') {
        const color = page.background_color ?? '#ffffff';
        if (now().background_kind !== 'none')
          design = await firstValueFrom(api.removeBackground(id, color));
        else if (now().background_color !== color)
          design = await firstValueFrom(api.setPageColor(id, color));
      } else if (page.background_kind === 'template') {
        if (
          now().background_kind !== 'template' ||
          now().background_template !== page.background_template
        )
          design = await firstValueFrom(
            api.setBackgroundTemplate(id, templateKeyForArtwork(page.background_template ?? '')),
          );
      } else if (now().file_id !== page.file_id) {
        const file = this.store.heldFile(page.file_id);
        if (!file) throw new Error('Die frühere Hintergrund-Datei ist nicht mehr vorhanden.');
        design = await firstValueFrom(api.setBackgroundFile(id, file));
        this.store.rememberFile(design.preview_url, file);
      }
      const landscape = page.page_width_pt > page.page_height_pt;
      if (
        design.background_kind === 'none' &&
        design.page_width_pt !== design.page_height_pt &&
        design.page_width_pt > design.page_height_pt !== landscape
      )
        design = await firstValueFrom(api.setOrientation(id, landscape));
      if (Math.abs(design.print_scale - page.print_scale) > 1e-6)
        design = await firstValueFrom(api.setPrintScale(id, page.print_scale));
      const layout: DesignLayout = { version: 2, elements: elements.map(cleanElement) };
      if (JSON.stringify(design.layout?.elements ?? []) !== JSON.stringify(layout.elements)) {
        try {
          design = await firstValueFrom(api.saveLayout(id, layout));
        } catch (error) {
          this.logger.error('Layout nach dem Zurücksetzen nicht gespeichert:', error);
        }
      }
      return design;
    } catch (error) {
      this.logger.error('Seite konnte nicht zurückgesetzt werden:', error);
      this.actionError.set(
        error instanceof Error && !('status' in error)
          ? error.message
          : designErrorMessage(error, 'Die Seite konnte nicht zurückgesetzt werden.'),
      );
      throw error;
    } finally {
      this.changingBackground.set(false);
    }
  }

  askActivate() {
    this.activateDialogOpen.set(true);
  }

  onActivateDialogState(state: 'open' | 'closed') {
    if (state === 'closed') this.activateDialogOpen.set(false);
  }

  confirmActivate() {
    this.activateDialogOpen.set(false);
    this.actionError.set(null);
    this.designService.activate(this.designId()).subscribe({
      next: () => {
        const design = this.store.design();
        if (design) this.store.design.set({ ...design, is_active: true });
      },
      error: (error) => {
        this.logger.error('Aktivieren fehlgeschlagen:', error);
        this.actionError.set(
          designErrorMessage(error, 'Das Design konnte nicht übernommen werden.'),
        );
      },
    });
  }

  // ------------------------------------------------------------- leaving

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.store.dirty()) event.preventDefault();
  }

  canLeave(): boolean | Promise<boolean> {
    if (!this.store.dirty()) return true;
    return new Promise<boolean>((resolve) => {
      this.leaveResolver = resolve;
      this.leaveDialogOpen.set(true);
    });
  }

  onLeaveDialogState(state: 'open' | 'closed') {
    if (state === 'closed' && this.leaveDialogOpen()) this.resolveLeave(false);
  }

  resolveLeave(leave: boolean) {
    this.leaveDialogOpen.set(false);
    this.leaveResolver?.(leave);
    this.leaveResolver = null;
  }
}
